<script setup>
import { nextTick, onMounted, ref } from "vue";
import { Search, Plus } from "@element-plus/icons-vue";
import AlarmRulesForm from "@/views/components/AlarmRulesForm.vue";
import GroupDevice from "@/views/components/GroupDevice.vue";
import { alarmList, publish, unpublish } from "@/api/alarmRules";
import { signData } from "@/api/device";
import { ElMessage } from "element-plus";

// 弹窗可见性控制
const dialogVisible = ref(false);

// 告警列表数据与参数
const newList = ref([]);
const total = ref(null);
const alarmParams = ref({
  pageSize: 10,
  pageNo: 1,
  alarmName: "",
});

// 控制状态变量
const background = ref(true);
const disabled = ref(false);

// 告警级别映射
const alarmLevelObj = {
  0: "alarm.attributeChange",
  1: "alarm.deviceOffLine",
};

// 签名数据对象
const signDataObj = ref({});

// 当前编辑的告警规则ID
const currentId = ref(null);

// 告警规则表单组件的引用
const alarmRules = ref(null);

// 获取告警规则列表
function getAlarmList(params) {
  alarmList(params).then((res) => {
    if (res && res.data && res.data.list) {
      total.value = res.data.total;
      newList.value = res.data.list;

      // 获取所有告警条件的属性标识数组
      const tslAttributes = res.data.list
        .filter((item) => item.conditions && item.conditions.length > 0) // 过滤掉 conditions 为空的项
        .flatMap((item) =>
          item.conditions.map((condition) => condition.tslAttribute)
        );

      // 获取属性详情
      getSignData(tslAttributes);
    }
  });
}

// 获取属性详情
function getSignData(list) {
  signData(list).then((res) => {
    signDataObj.value = res.data;
  });
}

// 添加新的告警规则
function addAlarm() {
  currentId.value = null;
  dialogVisible.value = true;
  alarmRules.value.initAlarmForm();
}

// 选择告警规则并查询
function selectAlarm() {
  getAlarmList(alarmParams.value);
}

// 关闭弹窗
function handleClose() {
  dialogVisible.value = false;
}

// 通过组下设备勾选获取告警规则
function deviceList(e) {
  alarmParams.value.executionId = e.join(",");
  getAlarmList(alarmParams.value);
}

// 编辑告警规则
async function editAlarm(item) {
  currentId.value = item.id;
  dialogVisible.value = true;

  // 等待组件挂载完成后调用子组件方法
  await nextTick();
  if (alarmRules.value) {
    alarmRules.value.getEditInfo();
  } else {
    console.error("alarmRules 组件未能正确引用");
  }
}

// 提交或取消操作后刷新列表
function submitCancel() {
  dialogVisible.value = false;
  getAlarmList(alarmParams.value);
}

// 处理每页显示数量变化
function handleSizeChange(val) {
  alarmParams.value.pageSize = val;
  getAlarmList(alarmParams.value);
}

// 处理当前页码变化
function handleCurrentChange(val) {
  alarmParams.value.pageNo = val;
  getAlarmList(alarmParams.value);
}

// 组件挂载后获取告警规则列表
onMounted(() => {
  getAlarmList(alarmParams.value);
});

// 打开告警
function openAlarm(item) {
  publish(item.id).then(() => {
    getAlarmList(alarmParams.value);
  });
}

// 关闭告警
function closeAlarm(item) {
  unpublish(item.id).then(() => {
    getAlarmList(alarmParams.value);
  });
}

// 时间戳转时分秒
function formatTimestamp(timestamp) {
  if (timestamp) {
    return Math.floor(timestamp / (1000 * 60)); // 将毫秒转换为分钟
  }
}
</script>
<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-9 col-12">
        <div class="card h-100">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <h5>{{ $t("alarm.alarmRule") }}</h5>
            <div class="d-flex align-items-center">
              <el-input
                v-model="alarmParams.alarmName"
                style="width: 180px"
                :placeholder="$t('alarm.searchTitle')"
                :prefix-icon="Search"
              />
              <el-button
                :icon="Search"
                type="primary"
                class="m-lg-1"
                @click="selectAlarm"
                >{{ $t("arg.query") }}</el-button
              >
              <el-button
                :icon="Plus"
                type="success"
                class="m-lg-1"
                @click="addAlarm"
                >{{ $t("alarm.add") }}</el-button
              >
              <el-dialog
                v-model="dialogVisible"
                :title="currentId ? $t('alarm.edit') : $t('alarm.add')"
                width="60%"
                :before-close="handleClose"
              >
                <AlarmRulesForm
                  ref="alarmRules"
                  :id="currentId"
                  @submitCancel="submitCancel"
                ></AlarmRulesForm>
              </el-dialog>
            </div>
          </div>
          <div class="card-body">
            <div class="row" v-if="newList && newList.length > 0">
              <div
                class="col-lg-4 col-md-6 col-sm-12 col-12 mt-2"
                v-for="(item, index) in newList"
                :key="index"
              >
                <div class="card h-100">
                  <div class="card-header">
                    <i class="no-inherit"></i> {{ item.alarmName }}
                  </div>
                  <div class="card-body">
                    <el-descriptions class="mt-2" title="" column="1">
                      <!--                      <el-descriptions-item :label="$t('alarm.alarmName')">{{item.alarmName}}</el-descriptions-item>-->
                      <el-descriptions-item
                        :label="$t('alarm.triggerType')"
                        v-if="alarmLevelObj[item.alarmLevel]"
                        >{{
                          $t(alarmLevelObj[item.alarmLevel])
                        }}</el-descriptions-item
                      >
                      <el-descriptions-item :label="$t('alarm.hitCount')">{{
                        item.hitCounts || "--"
                      }}</el-descriptions-item>
                      <el-descriptions-item :label="$t('alarm.duration')">
                        <el-tag size="small"
                          >{{ formatTimestamp(item.durations) || "--" }}
                        </el-tag>
                        <span v-if="item.durations">{{
                          $t("alarm.minute")
                        }}</span>
                      </el-descriptions-item>
                      <el-descriptions-item :label="$t('alarm.runningStatus')">
                        <el-tag
                          v-if="
                            item.deployState === 0 || item.deployState === 2
                          "
                          type="danger"
                        >
                          {{ $t("alarm.close") }}
                        </el-tag>
                        <el-tag v-if="item.deployState === 1" type="success">
                          {{ $t("alarm.open") }}
                        </el-tag>
                      </el-descriptions-item>
                      <el-descriptions-item :label="$t('alarm.alarmCondition')">
                        <div class="overflow-auto">
                          <div
                            v-if="item.conditions && item.conditions.length > 0"
                          >
                            <div
                              v-for="(it, idx) in item.conditions"
                              :key="idx"
                            >
                              <el-tag class="m-2">{{ it.andor }}</el-tag>
                              <el-tag
                                v-if="
                                  signDataObj && signDataObj[it.tslAttribute]
                                "
                              >
                                {{ signDataObj[it.tslAttribute].yeuName }}
                                {{ it.subconditions }}
                                {{ it.value }}
                              </el-tag>
                            </div>
                          </div>
                        </div>
                      </el-descriptions-item>
                    </el-descriptions>
                  </div>
                  <div class="card-footer">
                    <el-button type="primary" @click="editAlarm(item)">{{
                      $t("group.edit")
                    }}</el-button>
                    <el-button
                      v-if="item.deployState === 0 || item.deployState === 2"
                      type="success"
                      @click="openAlarm(item)"
                    >
                      {{ $t("alarm.handleOpen") }}
                    </el-button>
                    <el-button
                      v-if="item.deployState === 1"
                      type="danger"
                      @click="closeAlarm(item)"
                    >
                      {{ $t("alarm.close") }}
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
            <el-empty v-else :description="$t('home.noData')" />

            <div
              v-if="newList && newList.length > 0"
              class="p-3 d-flex align-items-center justify-content-end"
            >
              <el-pagination
                v-model:current-page="alarmParams.pageNo"
                v-model:page-size="alarmParams.pageSize"
                :page-sizes="[10, 100, 200, 300, 400]"
                :disabled="disabled"
                :background="background"
                layout="sizes, prev, pager, next"
                :total="total"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-3 col-12 mt-2">
        <div class="card h-100">
          <div class="card-header">
            <h5>{{ $t("home.deviceList") }}</h5>
          </div>
          <div class="card-body">
            <group-device @device-list="deviceList"></group-device>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.waring-bg {
  background: #f7f8fc;
}
</style>
